import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ConsultantNav } from "@/components/ConsultantNav";
import {
  Lock, Loader2, LogOut, ArrowLeft, Users, ShieldCheck, TrendingUp,
  ArrowRight, CheckCircle2, Send, Pencil, Save, RefreshCw, FileText, Building2,
  Download, Power, Flag, Plus, Copy, Sparkles, Ticket, Ban, KeyRound,
} from "lucide-react";
import {
  consultantLogin, listCohorts, getCohortReport, saveInterpretation, changeStage,
  toggleAfterPhase, downloadReportPdf, createCohort,
  createMentorCode, listMentorCodes, revokeMentorCode,
} from "@/lib/gfApi";

const PURPLE = "#4B006E";
const PURPLE_D = "#38004F";
const GOLD = "#D4AF37";
const TOKEN_KEY = "gf_consultant_token";

const bandColor = (band) => ({
  "Strong Foundation": "#10B981",
  "Developing Well": "#D4AF37",
  "Growth Opportunity": "#F59E0B",
  "Priority Attention": "#EF4444",
}[band] || GOLD);

const STAGE_ORDER = ["data_collection", "ready_for_review", "approved", "sent"];

/* ---------------- Login Gate ---------------- */
function LoginGate({ onLogin }) {
  const [passcode, setPasscode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!passcode.trim()) return;
    setLoading(true);
    try {
      const { token } = await consultantLogin(passcode.trim());
      sessionStorage.setItem(TOKEN_KEY, token);
      onLogin(token);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Log masuk gagal.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6" style={{ background: PURPLE_D }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-3xl p-8" style={{ background: PURPLE }} data-testid="consultant-login">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl" style={{ background: "rgba(255,255,255,0.1)" }}>
          <Lock className="h-7 w-7" style={{ color: GOLD }} />
        </span>
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.3em]" style={{ color: GOLD }}>Consultant Access</p>
        <h1 className="mt-2 font-heading text-3xl font-bold text-white">Papan Pemuka Consultant</h1>
        <p className="mt-2 text-sm" style={{ color: "#C9B2D6" }}>
          Masukkan kod laluan consultant untuk mengakses analisis agregat kohort.
        </p>
        <input
          data-testid="consultant-passcode-input"
          type="password"
          value={passcode}
          onChange={(e) => setPasscode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Kod laluan consultant"
          className="mt-6 w-full rounded-2xl border-2 bg-white/5 px-4 py-3.5 text-center text-white outline-none placeholder:text-white/30"
          style={{ borderColor: "rgba(212,175,55,0.4)" }}
        />
        <button
          data-testid="consultant-login-btn"
          onClick={submit}
          disabled={loading || !passcode.trim()}
          className="mt-5 flex w-full items-center justify-center gap-3 rounded-full py-4 text-base font-bold transition-transform active:scale-[0.98] disabled:opacity-50"
          style={{ background: GOLD, color: PURPLE }}
        >
          {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Mengesahkan…</> : "LOG MASUK"}
        </button>
      </motion.div>
    </div>
  );
}

/* ---------------- Cohort List ---------------- */
function CohortList({ token, onSelect, onLogout, onCreate, onMentor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listCohorts(token)
      .then(setData)
      .catch((e) => {
        if (e?.response?.status === 401) onLogout();
        else toast.error("Gagal memuatkan senarai kohort.");
      })
      .finally(() => setLoading(false));
  }, [token, onLogout]);

  if (loading) return <FullLoader />;

  const stageChip = (st, label) => {
    const c = { data_collection: "#9CA3AF", ready_for_review: GOLD, approved: "#10B981", sent: "#60A5FA" }[st] || GOLD;
    return <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${c}22`, color: c }}>{label}</span>;
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="cohort-list">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em]" style={{ color: GOLD }}>Consultant Dashboard</p>
          <h1 className="mt-1 font-heading text-3xl font-bold text-white">Analisis Kohort</h1>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="mentor-codes-btn" onClick={onMentor}
            className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-white/10"
            style={{ borderColor: "rgba(212,175,55,0.5)" }}>
            <Ticket className="h-4 w-4" style={{ color: GOLD }} /> Kod Akses Mentor
          </button>
          <button data-testid="create-cohort-btn" onClick={onCreate}
            className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-transform active:scale-95"
            style={{ background: GOLD, color: PURPLE }}>
            <Plus className="h-4 w-4" /> Cipta Kohort Baharu
          </button>
          <button data-testid="consultant-logout-btn" onClick={onLogout}
            className="flex items-center gap-2 rounded-full border px-4 py-2 text-sm text-white/80 transition-colors hover:bg-white/10"
            style={{ borderColor: "rgba(255,255,255,0.2)" }}>
            <LogOut className="h-4 w-4" /> Log keluar
          </button>
        </div>
      </div>

      <div className="mt-6">
        <ConsultantNav active="team" gold={GOLD} ink={PURPLE} />
      </div>

      {data.cohorts.length === 0 && (
        <p className="mt-8 text-sm" style={{ color: "#C9B2D6" }} data-testid="cohort-empty">
          Belum ada kohort. Klik “Cipta Kohort Baharu” untuk mula.
        </p>
      )}

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {data.cohorts.map((c) => (
          <motion.button
            key={c.id}
            data-testid={`cohort-card-${c.access_code}`}
            onClick={() => onSelect(c.access_code)}
            whileHover={{ y: -3 }}
            className="rounded-2xl p-5 text-left transition-shadow hover:shadow-xl"
            style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" style={{ color: GOLD }} />
                <span className="text-xs font-semibold" style={{ color: "#C9B2D6" }}>{c.organisation}</span>
              </div>
              {c.is_demo && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${GOLD}22`, color: GOLD }}>DEMO</span>}
            </div>
            <h3 className="mt-3 font-heading text-xl font-bold text-white">{c.cohort_name}</h3>
            <p className="text-sm" style={{ color: "#9E7EB0" }}>{c.programme}</p>
            <div className="mt-4 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-sm" style={{ color: "#C9B2D6" }}>
                <Users className="h-4 w-4" /> {c.registered}/{c.seats} peserta
              </span>
              {stageChip(c.report_status, c.report_status_label)}
            </div>
            <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: "#9E7EB0" }}>
              <span>Sebelum: {c.before_completed}</span>
              <span>Selepas: {c.after_completed}</span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Approval Gate ---------------- */
function ApprovalGate({ status, statusLabel, onAction, busy }) {
  const idx = STAGE_ORDER.indexOf(status);
  const nextMap = {
    data_collection: { action: "ready", label: "Tandakan Sedia untuk Semakan", icon: FileText },
    ready_for_review: { action: "approve", label: "Luluskan Laporan", icon: CheckCircle2 },
    approved: { action: "send", label: "Hantar ke Organisasi", icon: Send },
  };
  const next = nextMap[status];

  return (
    <div className="rounded-2xl p-6" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="approval-gate">
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Approval Gate</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {STAGE_ORDER.map((s, i) => {
          const done = i <= idx;
          const labels = { data_collection: "Pengumpulan Data", ready_for_review: "Sedia Semakan", approved: "Diluluskan", sent: "Dihantar" };
          return (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5">
                <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold"
                  style={{ background: done ? GOLD : "rgba(255,255,255,0.12)", color: done ? PURPLE : "#9E7EB0" }}>
                  {done ? "✓" : i + 1}
                </span>
                <span className="text-xs font-semibold" style={{ color: done ? "#fff" : "#9E7EB0" }}>{labels[s]}</span>
              </div>
              {i < STAGE_ORDER.length - 1 && <ArrowRight className="h-3 w-3" style={{ color: "#6B4880" }} />}
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {next && (
          <button
            data-testid="approval-next-btn"
            onClick={() => onAction(next.action)}
            disabled={busy}
            className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-transform active:scale-95 disabled:opacity-50"
            style={{ background: GOLD, color: PURPLE }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <next.icon className="h-4 w-4" />} {next.label}
          </button>
        )}
        {status === "sent" && (
          <span className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold" style={{ background: "#10B98122", color: "#10B981" }}>
            <CheckCircle2 className="h-4 w-4" /> Laporan telah dihantar ke organisasi
          </span>
        )}
        {(status === "ready_for_review" || status === "approved") && (
          <button
            data-testid="approval-reopen-btn"
            onClick={() => onAction("reopen")}
            disabled={busy}
            className="flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 disabled:opacity-50"
            style={{ borderColor: "rgba(255,255,255,0.2)" }}
          >
            <RefreshCw className="h-4 w-4" /> Buka semula
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------- Factor Bars ---------------- */
function FactorBars({ factors, hasAfter }) {
  return (
    <div className="space-y-4" data-testid="factor-bars">
      {factors.map((f) => (
        <div key={f.key}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-white">{f.label}</span>
            <span className="flex items-center gap-2">
              <span style={{ color: "#9E7EB0" }}>{f.pre}%</span>
              {hasAfter && (
                <>
                  <ArrowRight className="h-3 w-3" style={{ color: "#6B4880" }} />
                  <span className="font-bold" style={{ color: GOLD }}>{f.post}%</span>
                  <span className="rounded-full px-2 py-0.5 text-xs font-bold"
                    style={{ background: f.delta >= 0 ? "#10B98122" : "#EF444422", color: f.delta >= 0 ? "#34D399" : "#F87171" }}>
                    {f.delta >= 0 ? "+" : ""}{f.delta}
                  </span>
                </>
              )}
            </span>
          </div>
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="relative h-full">
              {hasAfter && <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${f.pre}%`, background: "rgba(255,255,255,0.25)" }} />}
              <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${hasAfter ? f.post : f.pre}%`, background: bandColor(f.band) }} />
            </div>
          </div>
        </div>
      ))}
      {hasAfter && (
        <div className="flex gap-4 pt-1 text-xs" style={{ color: "#9E7EB0" }}>
          <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full" style={{ background: "rgba(255,255,255,0.25)" }} /> Sebelum</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full" style={{ background: GOLD }} /> Selepas</span>
        </div>
      )}
    </div>
  );
}

/* ---------------- Cohort Detail ---------------- */
function CohortDetail({ code, token, onBack, onLogout }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [interp, setInterp] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getCohortReport(code, token)
      .then((r) => { setReport(r); setInterp(r.interpretation || ""); })
      .catch((e) => {
        if (e?.response?.status === 401) onLogout();
        else toast.error("Gagal memuatkan laporan.");
      })
      .finally(() => setLoading(false));
  }, [code, token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const doStage = async (action) => {
    setBusy(true);
    try {
      await changeStage(code, action, token);
      toast.success("Status laporan dikemas kini.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tindakan gagal.");
    } finally {
      setBusy(false);
    }
  };

  const doSave = async () => {
    setBusy(true);
    try {
      await saveInterpretation(code, interp, token);
      toast.success("Interpretasi disimpan.");
      setEditing(false);
      load();
    } catch (e) {
      toast.error("Gagal menyimpan interpretasi.");
    } finally {
      setBusy(false);
    }
  };

  const doToggleAfter = async () => {
    if (!report) return;
    setBusy(true);
    try {
      await toggleAfterPhase(code, !report.cohort.after_open, token);
      toast.success(report.cohort.after_open ? "Fasa After ditutup." : "Fasa After dibuka.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengemas kini fasa After.");
    } finally {
      setBusy(false);
    }
  };

  const doDownloadPdf = async () => {
    setBusy(true);
    try {
      const blob = await downloadReportPdf(code, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-kohort-${code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Laporan PDF dimuat turun.");
    } catch (e) {
      toast.error("Laporan hanya boleh dimuat turun selepas diluluskan.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <FullLoader />;
  if (!report) return null;

  const { cohort, participation, overall, factors, baseline, has_after, privacy_ok, min_n, message } = report;
  const locked = cohort.report_status === "sent";

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="cohort-detail">
      <button data-testid="detail-back-btn" onClick={onBack}
        className="flex items-center gap-1 text-sm" style={{ color: "#C9B2D6" }}>
        <ArrowLeft className="h-4 w-4" /> Semua kohort
      </button>

      {/* Header */}
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4" style={{ color: GOLD }} />
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#C9B2D6" }}>{cohort.organisation}</p>
            {cohort.is_demo && <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${GOLD}22`, color: GOLD }}>DEMO</span>}
          </div>
          <h1 className="mt-1 font-heading text-3xl font-bold text-white">{cohort.cohort_name}</h1>
          <p className="text-sm" style={{ color: "#9E7EB0" }}>{cohort.programme} · Kod: {cohort.access_code}</p>
        </div>
      </div>

      {/* Privacy notice */}
      <div className="mt-5 flex items-start gap-2 rounded-xl p-3" style={{ background: "rgba(255,255,255,0.05)" }}>
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />
        <p className="text-xs leading-relaxed" style={{ color: "#C9B2D6" }}>
          Semua data dipaparkan secara agregat. Jawapan dan identiti individu tidak pernah didedahkan (Ruang Selamat).
        </p>
      </div>

      {!privacy_ok ? (
        <div className="mt-8 rounded-2xl p-8 text-center" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="privacy-blocked">
          <Lock className="mx-auto h-8 w-8" style={{ color: GOLD }} />
          <p className="mt-4 text-sm" style={{ color: "#C9B2D6" }}>{message || `Sekurang-kurangnya ${min_n} respons diperlukan.`}</p>
        </div>
      ) : (
        <>
          {/* Approval Gate */}
          <div className="mt-6">
            <ApprovalGate status={cohort.report_status} statusLabel={cohort.report_status_label} onAction={doStage} busy={busy} />
          </div>

          {/* After phase control + PDF export */}
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="flex items-center justify-between rounded-2xl p-5" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="after-phase-control">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Fasa After Assessment</p>
                <p className="mt-1 text-sm" style={{ color: cohort.after_open ? "#34D399" : "#9E7EB0" }}>
                  {cohort.after_open ? "Dibuka — peserta boleh membuat penilaian Selepas" : "Ditutup — peserta belum boleh membuat penilaian Selepas"}
                </p>
              </div>
              <button data-testid="after-phase-toggle-btn" onClick={doToggleAfter} disabled={busy}
                className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-transform active:scale-95 disabled:opacity-50"
                style={cohort.after_open
                  ? { background: "rgba(255,255,255,0.12)", color: "#fff" }
                  : { background: GOLD, color: PURPLE }}>
                <Power className="h-4 w-4" /> {cohort.after_open ? "Tutup" : "Buka"}
              </button>
            </div>

            <div className="flex items-center justify-between rounded-2xl p-5" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="pdf-export-card">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Laporan PDF</p>
                <p className="mt-1 text-sm" style={{ color: "#9E7EB0" }}>
                  {["approved", "sent"].includes(cohort.report_status)
                    ? "Muat turun laporan agregat berjenama"
                    : "Tersedia selepas laporan diluluskan"}
                </p>
              </div>
              <button data-testid="download-pdf-btn" onClick={doDownloadPdf}
                disabled={busy || !["approved", "sent"].includes(cohort.report_status)}
                className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-transform active:scale-95 disabled:opacity-40"
                style={{ background: GOLD, color: PURPLE }}>
                <Download className="h-4 w-4" /> Muat Turun
              </button>
            </div>
          </div>

          {/* Participation + Overall */}
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <StatCard label="Peserta Berdaftar" value={`${participation.registered}/${participation.seats}`} sub="tempat diisi" />
            <StatCard label="Respons Sebelum" value={participation.before_completed} sub={`${participation.before_rate}% penyertaan`} />
            <StatCard label="Respons Selepas" value={participation.after_completed} sub={`${participation.after_rate}% penyertaan`} />
          </div>

          {/* Overall pre/post */}
          <div className="mt-4 rounded-2xl p-6" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="overall-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Skor Keseluruhan Kohort</p>
              {report.milestone && (
                <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" data-testid="milestone-chip"
                  style={{ background: "rgba(212,175,55,0.15)", color: GOLD }}>
                  <Flag className="h-3.5 w-3.5" /> {report.milestone.title}: {report.milestone.label}
                </span>
              )}
            </div>
            <div className="mt-4 flex items-end gap-6">
              <div>
                <p className="text-xs" style={{ color: "#9E7EB0" }}>Sebelum</p>
                <p className="font-heading text-4xl font-bold text-white">{overall.pre}%</p>
                <p className="text-xs" style={{ color: "#9E7EB0" }}>{overall.pre_band}</p>
              </div>
              {has_after && (
                <>
                  <ArrowRight className="mb-6 h-6 w-6" style={{ color: "#6B4880" }} />
                  <div>
                    <p className="text-xs" style={{ color: GOLD }}>Selepas</p>
                    <p className="font-heading text-4xl font-bold" style={{ color: GOLD }}>{overall.post}%</p>
                    <p className="text-xs" style={{ color: "#9E7EB0" }}>{overall.post_band}</p>
                  </div>
                  <div className="mb-6 flex items-center gap-1.5 rounded-full px-3 py-1"
                    style={{ background: overall.delta >= 0 ? "#10B98122" : "#EF444422" }}>
                    <TrendingUp className="h-4 w-4" style={{ color: overall.delta >= 0 ? "#34D399" : "#F87171" }} />
                    <span className="font-bold" style={{ color: overall.delta >= 0 ? "#34D399" : "#F87171" }}>
                      {overall.delta >= 0 ? "+" : ""}{overall.delta} mata
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Factor breakdown */}
          <div className="mt-4 rounded-2xl p-6" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Skor Mengikut Faktor (32 Soalan Teras)</p>
            <div className="mt-5"><FactorBars factors={factors} hasAfter={has_after} /></div>
          </div>

          {/* Baseline diagnostic */}
          <div className="mt-4 rounded-2xl p-6" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="baseline-card">
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Konteks Diagnostik Asas (Baseline)</p>
            <p className="mt-1 text-xs" style={{ color: "#9E7EB0" }}>Purata 1–5. Tidak diskorkan — digunakan untuk memahami punca & keperluan intervensi.</p>
            <div className="mt-4 space-y-3">
              {baseline.map((b) => (
                <div key={b.id} className="flex items-center gap-4">
                  <div className="flex-1 text-sm text-white">{b.text}</div>
                  <div className="flex w-40 items-center gap-2">
                    <div className="h-2 flex-1 overflow-hidden rounded-full" style={{ background: "rgba(255,255,255,0.08)" }}>
                      <div className="h-full rounded-full" style={{ width: `${(b.avg / 5) * 100}%`, background: GOLD }} />
                    </div>
                    <span className="w-8 text-right text-sm font-bold" style={{ color: GOLD }}>{b.avg}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Consultant interpretation */}
          <div className="mt-4 rounded-2xl p-6" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="interpretation-card">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>
                Interpretasi Consultant {report.interpretation_is_draft && !editing && <span className="ml-2 text-[10px]" style={{ color: "#9E7EB0" }}>(draf auto)</span>}
              </p>
              {!locked && !editing && (
                <button data-testid="edit-interpretation-btn" onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 text-sm" style={{ color: GOLD }}>
                  <Pencil className="h-4 w-4" /> Sunting
                </button>
              )}
            </div>
            {editing ? (
              <div className="mt-4">
                <textarea
                  data-testid="interpretation-textarea"
                  value={interp}
                  onChange={(e) => setInterp(e.target.value)}
                  rows={10}
                  className="w-full rounded-xl border bg-white/5 p-4 text-sm leading-relaxed text-white outline-none"
                  style={{ borderColor: "rgba(212,175,55,0.4)" }}
                />
                <div className="mt-3 flex gap-3">
                  <button data-testid="save-interpretation-btn" onClick={doSave} disabled={busy}
                    className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold disabled:opacity-50" style={{ background: GOLD, color: PURPLE }}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Simpan
                  </button>
                  <button onClick={() => { setEditing(false); setInterp(report.interpretation || ""); }}
                    className="rounded-full border px-5 py-2.5 text-sm text-white/80" style={{ borderColor: "rgba(255,255,255,0.2)" }}>
                    Batal
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-3 text-sm leading-relaxed" style={{ color: "#E5D4EE" }}>
                {(interp || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Create Cohort ---------------- */
function genCode(cohortName) {
  const base = (cohortName || "GF")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.slice(0, 3))
    .join("")
    .slice(0, 8) || "GF";
  const year = new Date().getFullYear();
  const rand = Math.random().toString(36).toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4);
  return `${base}-${year}-${rand}`;
}

const FIELD_CLS =
  "mt-1.5 w-full rounded-xl border-2 bg-white/5 px-4 py-3 text-sm text-white outline-none placeholder:text-white/30";
const FIELD_STYLE = { borderColor: "rgba(212,175,55,0.35)" };
const LABEL_CLS = "text-xs font-semibold uppercase tracking-wider";
const LABEL_STYLE = { color: "#C9B2D6" };

function CreateCohortForm({ token, onBack, onCreated }) {
  const [f, setF] = useState({
    organisation: "", programme: "", cohort_name: "",
    seats: "20", start_date: "", expiry_date: "", access_code: "",
  });
  const [autoCode, setAutoCode] = useState(true);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!f.organisation.trim() || !f.programme.trim() || !f.cohort_name.trim()) {
      toast.error("Sila lengkapkan Organisasi, Program dan Nama Kohort.");
      return;
    }
    const seatsNum = parseInt(f.seats, 10);
    if (!seatsNum || seatsNum < 1) {
      toast.error("Bilangan tempat mesti sekurang-kurangnya 1.");
      return;
    }
    let code = autoCode ? genCode(f.cohort_name) : f.access_code.trim().toUpperCase();
    if (!code) {
      toast.error("Sila masukkan GF Access Code atau guna Auto Jana.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        organisation: f.organisation.trim(),
        programme: f.programme.trim(),
        cohort_name: f.cohort_name.trim(),
        access_code: code,
        seats: seatsNum,
        start_date: f.start_date || "",
        expiry_date: f.expiry_date || "",
      };
      const res = await createCohort(payload, token);
      setCreated({ ...payload, ...res });
      toast.success("Kohort berjaya dicipta.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) { toast.error("Sesi tamat. Sila log masuk semula."); return; }
      toast.error(e?.response?.data?.detail || "Gagal mencipta kohort.");
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    const text = created.access_code;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Gagal menyalin kod. Sila salin secara manual.");
    }
  };

  if (created) {
    const Row = ({ k, v }) => (
      <div className="flex items-center justify-between gap-4 border-b py-2 last:border-0" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        <span className="text-xs" style={{ color: "#9E7EB0" }}>{k}</span>
        <span className="text-sm font-semibold text-white text-right">{v || "—"}</span>
      </div>
    );
    return (
      <div className="mx-auto max-w-lg px-6 py-10" data-testid="cohort-created">
        <div className="rounded-2xl p-7" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }}>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6" style={{ color: "#34D399" }} />
            <h1 className="font-heading text-2xl font-bold text-white">Kohort Dicipta</h1>
          </div>
          <div className="mt-5 space-y-1">
            <Row k="Organisasi" v={created.organisation} />
            <Row k="Program" v={created.programme} />
            <Row k="Kohort" v={created.cohort_name} />
            <Row k="Tempat (Seats)" v={String(created.seats)} />
            <Row k="Tarikh Tamat" v={created.expiry_date} />
          </div>
          <div className="mt-5 rounded-xl p-4" style={{ background: "rgba(212,175,55,0.1)" }}>
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: GOLD }}>GF Access Code</p>
            <div className="mt-2 flex items-center justify-between gap-3">
              <span data-testid="created-code" className="font-heading text-2xl font-bold tracking-widest text-white">{created.access_code}</span>
              <button data-testid="copy-code-btn" onClick={copyCode}
                className="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-transform active:scale-95"
                style={{ background: GOLD, color: PURPLE }}>
                {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Disalin" : "Salin Kod"}
              </button>
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button data-testid="created-done-btn" onClick={onCreated}
              className="flex-1 rounded-full py-3 text-sm font-bold" style={{ background: GOLD, color: PURPLE }}>
              Ke Senarai Kohort
            </button>
            <button onClick={() => { setCreated(null); setF({ organisation: "", programme: "", cohort_name: "", seats: "20", start_date: "", expiry_date: "", access_code: "" }); }}
              className="rounded-full border px-5 py-3 text-sm text-white/80" style={{ borderColor: "rgba(255,255,255,0.2)" }}>
              Cipta Lagi
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-6 py-10" data-testid="create-cohort-form">
      <button data-testid="create-back-btn" onClick={onBack}
        className="flex items-center gap-1 text-sm" style={{ color: "#C9B2D6" }}>
        <ArrowLeft className="h-4 w-4" /> Semua kohort
      </button>
      <h1 className="mt-4 font-heading text-3xl font-bold text-white">Cipta Kohort Baharu</h1>
      <p className="mt-1 text-sm" style={{ color: "#9E7EB0" }}>Menggunakan sistem kohort sedia ada — Before/After, analisis agregat, privasi dan PDF berjenama.</p>

      <div className="mt-6 space-y-4">
        <div>
          <label className={LABEL_CLS} style={LABEL_STYLE}>Nama Organisasi</label>
          <input data-testid="cc-organisation" value={f.organisation} onChange={set("organisation")} placeholder="cth: Perbadanan Usahawan Johor Berhad" className={FIELD_CLS} style={FIELD_STYLE} />
        </div>
        <div>
          <label className={LABEL_CLS} style={LABEL_STYLE}>Nama Program</label>
          <input data-testid="cc-programme" value={f.programme} onChange={set("programme")} placeholder="cth: Program Transformasi Pasukan" className={FIELD_CLS} style={FIELD_STYLE} />
        </div>
        <div>
          <label className={LABEL_CLS} style={LABEL_STYLE}>Nama Kohort</label>
          <input data-testid="cc-cohort-name" value={f.cohort_name} onChange={set("cohort_name")} placeholder="cth: PUJB August 2026" className={FIELD_CLS} style={FIELD_STYLE} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={LABEL_CLS} style={LABEL_STYLE}>Tempat</label>
            <input data-testid="cc-seats" type="number" min="1" value={f.seats} onChange={set("seats")} className={FIELD_CLS} style={FIELD_STYLE} />
          </div>
          <div>
            <label className={LABEL_CLS} style={LABEL_STYLE}>Tarikh Mula</label>
            <input data-testid="cc-start-date" type="date" value={f.start_date} onChange={set("start_date")} className={FIELD_CLS} style={FIELD_STYLE} />
          </div>
          <div>
            <label className={LABEL_CLS} style={LABEL_STYLE}>Tarikh Tamat</label>
            <input data-testid="cc-expiry-date" type="date" value={f.expiry_date} onChange={set("expiry_date")} className={FIELD_CLS} style={FIELD_STYLE} />
          </div>
        </div>

        <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
          <div className="flex items-center justify-between">
            <label className={LABEL_CLS} style={LABEL_STYLE}>GF Access Code</label>
            <div className="flex gap-1 rounded-full p-1" style={{ background: "rgba(0,0,0,0.2)" }}>
              <button data-testid="cc-mode-auto" onClick={() => setAutoCode(true)}
                className="rounded-full px-3 py-1 text-xs font-bold transition-colors"
                style={autoCode ? { background: GOLD, color: PURPLE } : { color: "#C9B2D6" }}>
                <Sparkles className="mr-1 inline h-3 w-3" /> Auto Jana
              </button>
              <button data-testid="cc-mode-custom" onClick={() => setAutoCode(false)}
                className="rounded-full px-3 py-1 text-xs font-bold transition-colors"
                style={!autoCode ? { background: GOLD, color: PURPLE } : { color: "#C9B2D6" }}>
                <Pencil className="mr-1 inline h-3 w-3" /> Kod Tersendiri
              </button>
            </div>
          </div>
          {autoCode ? (
            <p className="mt-3 text-xs" style={{ color: "#9E7EB0" }} data-testid="cc-auto-note">
              Kod unik akan dijana secara automatik semasa disimpan (cth: PUJBAUG-2026-X7K2). Keunikan disahkan oleh sistem.
            </p>
          ) : (
            <input data-testid="cc-custom-code" value={f.access_code}
              onChange={(e) => setF((s) => ({ ...s, access_code: e.target.value.toUpperCase() }))}
              placeholder="cth: PUJB-2026-A01"
              className={`${FIELD_CLS} uppercase tracking-widest font-bold`} style={FIELD_STYLE} />
          )}
        </div>

        <button data-testid="cc-submit-btn" onClick={submit} disabled={busy}
          className="flex w-full items-center justify-center gap-2 rounded-full py-4 text-base font-bold transition-transform active:scale-[0.98] disabled:opacity-50"
          style={{ background: GOLD, color: PURPLE }}>
          {busy ? <><Loader2 className="h-5 w-5 animate-spin" /> Menyimpan…</> : <><Plus className="h-5 w-5" /> Cipta Kohort</>}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Mentor / Program Access Codes ---------------- */
const ACCESS_LABELS = { business: "Business Growth", personal: "Personal Growth", both: "Kedua-dua Laluan" };

function MentorCodesPanel({ token, onBack, onLogout }) {
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ label: "", max_uses: "1", expiry: "", access_type: "both" });
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(() => {
    listMentorCodes(token)
      .then((r) => setCodes(r.mentor_codes))
      .catch((e) => {
        if (e?.response?.status === 401) onLogout();
        else toast.error("Gagal memuatkan kod akses mentor.");
      });
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const mu = parseInt(form.max_uses, 10);
    if (!mu || mu < 1) { toast.error("Had penggunaan mesti sekurang-kurangnya 1."); return; }
    setBusy(true);
    try {
      await createMentorCode(
        { label: form.label.trim(), max_uses: mu, expiry: form.expiry || "", access_type: form.access_type },
        token
      );
      toast.success("Kod program dicipta.");
      setForm({ label: "", max_uses: "1", expiry: "", access_type: "both" });
      load();
    } catch (e) {
      if (e?.response?.status === 401) { onLogout(); return; }
      toast.error(e?.response?.data?.detail || "Gagal mencipta kod.");
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await revokeMentorCode(id, token); toast.success("Kod dibatalkan."); load(); }
    catch { toast.error("Gagal membatalkan kod."); }
  };

  const copy = async (code, id) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement("textarea");
        ta.value = code; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy"); ta.remove();
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch { toast.error("Gagal menyalin kod."); }
  };

  return (
    <div className="mx-auto max-w-4xl px-6 py-10" data-testid="mentor-codes-panel">
      <button data-testid="mentor-back-btn" onClick={onBack}
        className="flex items-center gap-1 text-sm" style={{ color: "#C9B2D6" }}>
        <ArrowLeft className="h-4 w-4" /> Semua kohort
      </button>
      <div className="mt-4 flex items-center gap-2">
        <Ticket className="h-5 w-5" style={{ color: GOLD }} />
        <h1 className="font-heading text-3xl font-bold text-white">Kod Akses Mentor</h1>
      </div>
      <p className="mt-1 text-sm" style={{ color: "#9E7EB0" }}>
        Kod berasingan yang membenarkan peserta program mentor/latihan membuka laporan penuh tanpa bayaran RM15. Boleh dibatalkan dan mempunyai had penggunaan. Tidak menjejaskan GF Access Code kohort.
      </p>

      {/* Create form */}
      <div className="mt-6 rounded-2xl p-6" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }} data-testid="mentor-create-form">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: GOLD }}>Cipta Kod Baharu</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={LABEL_CLS} style={LABEL_STYLE}>Label / Nama Program</label>
            <input data-testid="mc-label" value={form.label} onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
              placeholder="cth: Program Mentor Kohort Julai" className={FIELD_CLS} style={FIELD_STYLE} />
          </div>
          <div>
            <label className={LABEL_CLS} style={LABEL_STYLE}>Had Penggunaan</label>
            <input data-testid="mc-max-uses" type="number" min="1" value={form.max_uses}
              onChange={(e) => setForm((s) => ({ ...s, max_uses: e.target.value }))} className={FIELD_CLS} style={FIELD_STYLE} />
          </div>
          <div>
            <label className={LABEL_CLS} style={LABEL_STYLE}>Tarikh Tamat (pilihan)</label>
            <input data-testid="mc-expiry" type="date" value={form.expiry}
              onChange={(e) => setForm((s) => ({ ...s, expiry: e.target.value }))} className={FIELD_CLS} style={FIELD_STYLE} />
          </div>
          <div className="sm:col-span-2">
            <label className={LABEL_CLS} style={LABEL_STYLE}>Laluan</label>
            <select data-testid="mc-access-type" value={form.access_type}
              onChange={(e) => setForm((s) => ({ ...s, access_type: e.target.value }))}
              className={FIELD_CLS} style={FIELD_STYLE}>
              <option value="both" style={{ color: "#000" }}>Kedua-dua Laluan (Business + Personal)</option>
              <option value="business" style={{ color: "#000" }}>Business Growth sahaja</option>
              <option value="personal" style={{ color: "#000" }}>Personal Growth sahaja</option>
            </select>
          </div>
        </div>
        <button data-testid="mc-submit-btn" onClick={create} disabled={busy}
          className="mt-5 flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-transform active:scale-95 disabled:opacity-50"
          style={{ background: GOLD, color: PURPLE }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Cipta Kod
        </button>
      </div>

      {/* List */}
      <div className="mt-6" data-testid="mentor-codes-list">
        {codes === null ? (
          <FullLoader />
        ) : codes.length === 0 ? (
          <p className="text-sm" style={{ color: "#9E7EB0" }} data-testid="mentor-empty">Belum ada kod. Cipta kod pertama di atas.</p>
        ) : (
          <div className="space-y-3">
            {codes.map((c) => {
              const exhausted = c.used_count >= c.max_uses;
              const active = c.status === "active";
              return (
                <div key={c.id} data-testid={`mentor-code-row-${c.code}`}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-2xl p-5"
                  style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)", opacity: active ? 1 : 0.6 }}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <KeyRound className="h-4 w-4 shrink-0" style={{ color: GOLD }} />
                      <span className="font-heading text-lg font-bold tracking-widest text-white">{c.code}</span>
                      <button data-testid={`mentor-copy-${c.code}`} onClick={() => copy(c.code, c.id)}
                        className="rounded-full p-1.5 transition-colors hover:bg-white/10">
                        {copiedId === c.id ? <CheckCircle2 className="h-4 w-4" style={{ color: "#34D399" }} /> : <Copy className="h-4 w-4" style={{ color: "#C9B2D6" }} />}
                      </button>
                    </div>
                    {c.label && <p className="mt-1 text-sm" style={{ color: "#C9B2D6" }}>{c.label}</p>}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" style={{ color: "#9E7EB0" }}>
                      <span className="rounded-full px-2 py-0.5" style={{ background: "rgba(212,175,55,0.12)", color: GOLD }}>{ACCESS_LABELS[c.access_type] || c.access_type}</span>
                      <span>Guna: <b style={{ color: exhausted ? "#F87171" : "#fff" }}>{c.used_count}/{c.max_uses}</b></span>
                      {c.expiry && <span>Tamat: {c.expiry}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {!active ? (
                      <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: "#EF444422", color: "#F87171" }}>Dibatalkan</span>
                    ) : exhausted ? (
                      <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: "rgba(255,255,255,0.1)", color: "#9E7EB0" }}>Had Penuh</span>
                    ) : (
                      <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: "#10B98122", color: "#34D399" }}>Aktif</span>
                    )}
                    {active && (
                      <button data-testid={`mentor-revoke-${c.code}`} onClick={() => revoke(c.id)}
                        className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold text-white/80 transition-colors hover:bg-white/10"
                        style={{ borderColor: "rgba(248,113,113,0.4)" }}>
                        <Ban className="h-3.5 w-3.5" /> Batal
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ label, value, sub }) => (
  <div className="rounded-2xl p-5" style={{ background: PURPLE, border: "1px solid rgba(212,175,55,0.25)" }}>
    <p className="text-xs" style={{ color: "#9E7EB0" }}>{label}</p>
    <p className="mt-1 font-heading text-2xl font-bold text-white">{value}</p>
    <p className="text-xs" style={{ color: "#9E7EB0" }}>{sub}</p>
  </div>
);

const FullLoader = () => (
  <div className="flex min-h-[60vh] items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin" style={{ color: GOLD }} />
  </div>
);

/* ---------------- Root ---------------- */
export default function ConsultantDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [mentor, setMentor] = useState(false);
  const [listKey, setListKey] = useState(0);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setSelected(null);
    setCreating(false);
    setMentor(false);
  }, []);

  return (
    <div className="min-h-screen font-body" style={{ background: PURPLE_D }} data-testid="consultant-dashboard">
      {!token ? (
        <LoginGate onLogin={setToken} />
      ) : mentor ? (
        <MentorCodesPanel token={token} onBack={() => setMentor(false)} onLogout={logout} />
      ) : creating ? (
        <CreateCohortForm
          token={token}
          onBack={() => setCreating(false)}
          onCreated={() => { setCreating(false); setListKey((k) => k + 1); }}
        />
      ) : selected ? (
        <CohortDetail code={selected} token={token} onBack={() => setSelected(null)} onLogout={logout} />
      ) : (
        <CohortList key={listKey} token={token} onSelect={setSelected} onLogout={logout} onCreate={() => setCreating(true)} onMentor={() => setMentor(true)} />
      )}
    </div>
  );
}
